const PHONE_EXTENSION_MARKER_RE =
  /\s*(?:;ext=|(?:extension|ext\.?|x|#)\s*)\s*([0-9A-Da-d*#][0-9A-Da-d*#\s.-]{0,30})\s*$/i;

export const PHONE_EXTENSION_FIELDS = Object.freeze({
  phone: 'phoneExtension',
  mobilePhone: 'mobilePhoneExtension',
  officePhone: 'officePhoneExtension',
  mainOfficePhone: 'mainOfficePhoneExtension',
});

/**
 * Return the extension field that belongs to a phone field.
 *
 * @param {string} phoneField - Canonical phone field name.
 * @returns {string} Matching extension field name.
 */
export function extensionFieldForPhoneField(phoneField) {
  return PHONE_EXTENSION_FIELDS[phoneField] || `${phoneField}Extension`;
}

/**
 * Compact extension digits into a DTMF-safe value.
 *
 * @param {unknown} value - Raw extension-like value.
 * @returns {string} Sanitized extension digits, `*`, `#`, or A-D tones.
 */
export function sanitizePhoneExtension(value) {
  return String(value ?? '')
    .replace(/[^0-9A-Da-d*#]/g, '')
    .toUpperCase()
    .slice(0, 16);
}

/**
 * Normalize a base phone number to E.164 when it is unambiguous.
 *
 * @param {unknown} value - Raw phone number without an extension suffix.
 * @param {{ defaultCountryCode?: string }} [options={}] - Normalization options.
 * @returns {string} E.164 phone number or an empty string when unusable.
 */
export function normalizePhoneBase(value, options = {}) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const defaultCountryCode = String(options.defaultCountryCode || '+1')
    .replace(/[^\d+]/g, '')
    .replace(/^\+?/, '+');
  const digits = raw.replace(/\D+/g, '');
  if (!digits) return '';

  if (raw.startsWith('+')) {
    const e164 = `+${digits}`;
    return /^\+[1-9]\d{9,14}$/.test(e164) ? e164 : '';
  }

  if (digits.length === 10 && defaultCountryCode === '+1') {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  if (digits.length >= 10 && digits.length <= 15) {
    return `+${digits}`;
  }

  return '';
}

/**
 * Split a raw phone value into base number and explicit extension.
 *
 * @param {unknown} value - Raw phone field value.
 * @param {{ defaultCountryCode?: string }} [options={}] - Normalization options.
 * @returns {{ raw: string, baseRaw: string, phone: string, extension: string, hadExplicitExtension: boolean }}
 */
export function normalizePhoneNumberWithExtension(value, options = {}) {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return {
      raw: '',
      baseRaw: '',
      phone: '',
      extension: '',
      hadExplicitExtension: false,
    };
  }

  const match = PHONE_EXTENSION_MARKER_RE.exec(raw);
  const extension = match ? sanitizePhoneExtension(match[1]) : '';
  const baseRaw = match ? raw.slice(0, match.index).trim() : raw;
  const phone = normalizePhoneBase(baseRaw, options);

  return {
    raw,
    baseRaw,
    phone,
    extension,
    hadExplicitExtension: Boolean(match && extension),
  };
}

/**
 * Build the DTMF payload for sending an extension after answer.
 *
 * @param {unknown} extension - Raw extension value.
 * @param {{ pauseCount?: number, appendHash?: boolean }} [options={}] - Dial options.
 * @returns {string} Twilio sendDigits-compatible sequence.
 */
export function buildExtensionDialDigits(extension, options = {}) {
  const sanitized = sanitizePhoneExtension(extension);
  if (!sanitized) return '';
  const pauseCount = Math.max(0, Math.min(12, Number(options.pauseCount ?? 4)));
  const pauses = 'w'.repeat(pauseCount);
  const suffix =
    options.appendHash === true && !sanitized.endsWith('#') ? '#' : '';
  return `${pauses}${sanitized}${suffix}`;
}
